import { route } from 'preact-router';
import { Button } from '../../components/ui/Button.js';
import { mytDateTime } from '../../utils/mytTime.js';

/**
 * 374 (#1238): the booking page's "Car condition photos" card. Two steps, At pickup and At return, each in the state
 * the platform decided (`photo_windows` on `GET /bookings/:id`, from `backend/src/modules/bookings/photoWindows.ts`).
 * The page never works a window out itself: the send is refused exactly when this card says it is not open.
 */
export type PhotoWindowState = 'later' | 'open' | 'sent' | 'missed' | 'unavailable';

export interface PhotoWindow {
  state: PhotoWindowState;
  opens_at: string;
  closes_at: string;
  sent_at?: string;
  photo_count?: number;
  damage_count?: number;
}

export interface PhotoWindows {
  pickup: PhotoWindow;
  return: PhotoWindow;
  /** 415 (#1520): the fuel photo after the pickup set (absent from a payload older than 415). */
  fuel?: PhotoWindow;
}

type Moment = 'pickup' | 'return' | 'fuel';

/** 415: the Fuel step shows once the pickup set is in, and only while it means something to the customer. */
const showsFuel = (w?: PhotoWindow): w is PhotoWindow =>
  w?.state === 'open' || w?.state === 'sent' || w?.state === 'missed';

const CHIP: Record<PhotoWindowState, { label: string; className: string }> = {
  open: { label: 'Due now', className: 'bg-primary/15 text-primary' },
  sent: { label: 'Sent', className: 'bg-green-500/15 text-green-400' },
  later: { label: 'Later', className: 'bg-surface-mid text-text-muted' },
  missed: { label: 'Not sent', className: 'bg-warning/15 text-warning' },
  unavailable: { label: 'Not available', className: 'bg-surface-mid text-text-muted' },
};

/** The line under a step: what the customer can do, and until when. */
export function stepLine(moment: Moment, w: PhotoWindow, overdue = false): string {
  // 415: the fuel photo is one photo, asked once inside the pickup window.
  if (moment === 'fuel' && w.state === 'open')
    return `Open until ${mytDateTime(w.closes_at)} · 1 photo`;
  if (moment === 'fuel' && w.state === 'sent')
    return `${w.sent_at ? mytDateTime(w.sent_at) : 'Sent'} · 1 photo`;
  switch (w.state) {
    case 'open':
      // 407 (#1370): a late rental's return photos stay open while it is active; 414: sending them ends it.
      if (moment === 'return' && overdue) return 'Open until you send them';
      return `Open until ${mytDateTime(w.closes_at)} · 6 photos, about 2 minutes`;
    case 'later':
      return moment === 'pickup'
        ? `Opens ${mytDateTime(w.opens_at)}, just before your pickup`
        : `Opens ${mytDateTime(w.opens_at)}, before your return`;
    case 'sent': {
      const count = w.photo_count ?? 0;
      return `${w.sent_at ? mytDateTime(w.sent_at) : 'Sent'} · ${count} ${count === 1 ? 'photo' : 'photos'}`;
    }
    case 'missed':
      return `Closed ${mytDateTime(w.closes_at)}`;
    default:
      return 'Photos cannot be sent for this booking.';
  }
}

function Step({
  n,
  title,
  moment,
  w,
  overdue = false,
}: {
  n: number;
  title: string;
  moment: Moment;
  w: PhotoWindow;
  overdue?: boolean;
}) {
  const chip = CHIP[w.state];
  const dot =
    w.state === 'sent'
      ? 'bg-green-500/15 text-green-400'
      : w.state === 'open'
        ? 'bg-primary text-on-primary'
        : 'bg-surface-mid text-text-muted';
  return (
    <div data-testid={`photo-step-${moment}`} className="flex items-start gap-3 py-2">
      <span
        className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-sm font-bold ${dot}`}
      >
        {w.state === 'sent' ? '✓' : n}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{title}</span>
          <span
            data-testid={`photo-chip-${moment}`}
            className={`rounded-full px-2 py-0.5 text-xs font-bold ${chip.className}`}
          >
            {chip.label}
          </span>
        </div>
        <p data-testid={`photo-line-${moment}`} className="text-xs text-text-muted">
          {stepLine(moment, w, overdue)}
        </p>
      </div>
    </div>
  );
}

/**
 * `rentalId` is the original booking's id: both sets belong to the rental, whichever leg this page shows.
 */
export function CarConditionPhotosCard({
  rentalId,
  windows,
  overdue = false,
  endWaitsOnPayment = false,
}: {
  rentalId: string;
  windows: PhotoWindows;
  /** 407: the rental is checked out past its return (`return_overdue`). */
  overdue?: boolean;
  /** 414: the return photos are in, but a payment still holds the end back (`end_waits_on_payment`). */
  endWaitsOnPayment?: boolean;
}) {
  return (
    <div data-testid="car-condition-photos" className="mt-4 rounded-xl bg-surface-card p-4">
      <h3 className="mb-1 font-semibold">Car condition photos</h3>
      <p className="mb-2 text-xs text-text-muted">
        Protect yourself: a few photos at pickup and at return show the car's condition when you had
        it.
      </p>
      <Step n={1} title="At pickup" moment="pickup" w={windows.pickup} />
      {showsFuel(windows.fuel) && <Step n={2} title="Fuel" moment="fuel" w={windows.fuel} />}
      <Step
        n={showsFuel(windows.fuel) ? 3 : 2}
        title="At return"
        moment="return"
        w={windows.return}
        overdue={overdue}
      />
      {windows.pickup.state === 'open' && (
        <Button
          data-testid="inspect-checkout-cta"
          onClick={() => route(`/booking/${rentalId}/inspection/checkout`)}
          size="lg"
          className="mt-2 w-full"
        >
          Take pickup photos
        </Button>
      )}
      {windows.fuel?.state === 'open' && (
        <Button
          data-testid="inspect-fuel-cta"
          onClick={() => route(`/booking/${rentalId}/fuel`)}
          size="lg"
          className="mt-2 w-full"
        >
          Take fuel photo
        </Button>
      )}
      {windows.return.state === 'open' && (
        <Button
          data-testid="inspect-checkin-cta"
          onClick={() => route(`/booking/${rentalId}/inspection/checkin`)}
          size="lg"
          className="mt-2 w-full"
        >
          Take return photos
        </Button>
      )}
      {/* 414 (#1522): the return photos end the booking (407's End booking is gone from here); only a payment in
          progress can hold the end back, and the platform ends it once the payment is over. */}
      {windows.return.state === 'sent' && endWaitsOnPayment && (
        <p data-testid="end-waits-on-payment" className="mt-2 text-sm text-text-muted">
          Your booking will end once your payment finishes.
        </p>
      )}
    </div>
  );
}
